
import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, TrendingUp, ArrowRightLeft, Award, Search, Loader2, Info, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { getLatestTransfers, getTopTransferGroups, getTransfersByGroupId } from '../../services/footballService';
import { TransferNews, TransferGroup } from '../../types';
import { t } from '../../i18n';
import { TransferCard } from '../Home/TransferCard';
import SEO from '../Common/SEO';

interface CategoryListViewProps {
    type: 'top_transfers' | 'latest_movements';
    onBack: () => void;
    onNavigateToPlayer: (id: string) => void;
}

const LEAGUE_FILTERS = [
    { id: 'All', label: 'All' },
    { id: 'Premier League', label: 'EPL' },
    { id: 'LaLiga', label: 'La Liga' },
    { id: 'Bundesliga', label: 'Bundesliga' },
    { id: 'Serie A', label: 'Serie A' },
    { id: 'Ligue 1', label: 'Ligue 1' },
    { id: 'Saudi Pro League', label: 'SPL' },
];

export const CategoryListView: React.FC<CategoryListViewProps> = ({ type, onBack, onNavigateToPlayer }) => {
    const { lang } = useLanguage();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<any[]>([]);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [groups, setGroups] = useState<any[]>([]);
    const [activeGroup, setActiveGroup] = useState<any>(null);
    const [activeLeague, setActiveLeague] = useState<string>('All');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadInitialData();
    }, [type]);

    const loadInitialData = async () => {
        setLoading(true);
        setPage(0);
        setHasMore(true);
        try {
            if (type === 'latest_movements' || type === 'top_transfers') {
                const [latest, transferGroups] = await Promise.all([
                    getLatestTransfers(0, 50),
                    getTopTransferGroups()
                ]);
                
                setGroups(transferGroups || []);
                
                if (type === 'latest_movements') {
                    setData(latest || []);
                    setHasMore(latest.length === 50);
                    setActiveGroup({ id: 'latest', season: 'LATEST' });
                } else if (transferGroups && transferGroups.length > 0) {
                    setActiveGroup(transferGroups[0]);
                    const initialData = await getTransfersByGroupId(transferGroups[0].id);
                    setData(initialData || []);
                }
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleGroupChange = async (group: any) => {
        setLoading(true);
        setActiveGroup(group);
        setPage(0);
        setHasMore(true);
        try {
            if (group.id === 'latest') {
                const latest = await getLatestTransfers(0, 50);
                setData(latest || []);
                setHasMore(latest.length === 50);
            } else if (type === 'top_transfers' || type === 'latest_movements') {
                const newData = await getTransfersByGroupId(group.id);
                setData(newData || []);
            }
        } finally {
            setLoading(false);
        }
    };

    const filteredData = useMemo(() => {
        let results = [...data];

        // ID 오름차순 정렬 추가 (주요 이적 및 시장 가치용)
        // 최신(latest) 그룹이 아닐 때 오름차순 정렬
        if (activeGroup?.id !== 'latest') {
            results.sort((a, b) => {
                const idA = parseInt(a.id) || 0;
                const idB = parseInt(b.id) || 0;
                return idA - idB;
            });
        } else {
            // 최근 이적은 날짜 최신순으로 정렬 (날짜가 같은 경우 ID 내림차순으로 보조 정렬)
            results.sort((a, b) => {
                const parseDate = (d: string | undefined) => {
                    if (!d) return 0;
                    if (d.includes('/')) {
                        const [day, month, year] = d.split('/').map(Number);
                        return new Date(year, month - 1, day).getTime();
                    }
                    return new Date(d).getTime();
                };

                const dateA = parseDate(a.date);
                const dateB = parseDate(b.date);
                
                if (dateB !== dateA) {
                    return dateB - dateA;
                }
                
                // 날짜가 같으면 최신 등록순(ID 내림차순)으로 표시
                const idA = parseInt(a.id) || 0;
                const idB = parseInt(b.id) || 0;
                return idB - idA;
            });
        }

        // 리그 필터링 (주요 이적 전용)
        if ((type === 'top_transfers' || type === 'latest_movements') && activeLeague !== 'All') {
            results = results.filter(t => {
                const leagueName = t.league?.toLowerCase().replace(/\s+/g, '') || '';
                const filterName = activeLeague.toLowerCase().replace(/\s+/g, '');
                return leagueName.includes(filterName);
            });
        }

        // 검색 필터링
        if (searchTerm.trim()) {
            const q = searchTerm.toLowerCase();
            results = results.filter(item => {
                const name = (item.player?.name || item.player_name || '').toLowerCase();
                const club = (item.toClub?.name || item.team_name || '').toLowerCase();
                return name.includes(q) || club.includes(q);
            });
        }

        return results;
    }, [data, searchTerm, activeLeague, type]);

    const getTitle = () => {
        if (type === 'top_transfers' || type === 'latest_movements') return t(lang, 'latestMovements');
        return '';
    };

    const loadMore = async () => {
        if (loading || !hasMore || activeGroup?.id !== 'latest') return;
        setLoading(true);
        const nextPage = page + 1;
        try {
            const moreData = await getLatestTransfers(nextPage, 50);
            if (moreData.length > 0) {
                setData(prev => [...prev, ...moreData]);
                setPage(nextPage);
                setHasMore(moreData.length === 50);
            } else {
                setHasMore(false);
            }
        } catch (e) {
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    };

    const getIcon = () => {
        if (type === 'top_transfers') return <Award className="text-rose-500" />;
        if (type === 'latest_movements') return <ArrowRightLeft className="text-rose-500" />;
        return <TrendingUp className="text-emerald-500" />;
    };

    const seoConfig = useMemo(() => {
        if (type === 'top_transfers') {
            return {
                title: t(lang, 'topTransfers'),
                description: t(lang, 'topTransfersSeoDesc')
            };
        }
        return {
            title: t(lang, 'latestMovements'),
            description: t(lang, 'latestMovementsSeoDesc')
        };
    }, [type, lang]);

    return (
        <div className="min-h-screen bg-white pb-32 animate-in fade-in duration-500">
            <SEO title={seoConfig.title} description={seoConfig.description} />
            <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100 px-4 h-16 flex items-center gap-4">
                <button onClick={onBack} className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-50 text-slate-900 active:scale-90 transition-all">
                    <ArrowLeft size={20} strokeWidth={3} />
                </button>
                <div className="flex items-center gap-3">
                    {/* {getIcon()} */}
                    <h1 className="text-xl font-black text-slate-900 uppercase tracking-tight">{getTitle()}</h1>
                </div>
            </header>

            <div className="p-4 md:p-8 max-w-4xl mx-auto space-y-8">
                {/* Search & Main Group Filters */}
                <div className="space-y-4">
                    <div className="flex flex-col gap-4">
                        {/* 시즌/그룹 필터 */}
                        {(type === 'latest_movements' || type === 'top_transfers') && (
                            <div className="flex gap-2 overflow-x-auto no-scrollbar py-1">
                                <button 
                                    onClick={() => handleGroupChange({ id: 'latest', season: 'LATEST' })}
                                    className={`shrink-0 px-5 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeGroup?.id === 'latest' ? 'bg-rose-500 text-white shadow-sm' : 'bg-slate-50 text-slate-400 md:hover:bg-slate-100'}`}
                                >
                                    {t(lang, 'latest')}
                                </button>
                                {groups.map(g => (
                                    <button 
                                        key={g.id} 
                                        onClick={() => handleGroupChange(g)}
                                        className={`shrink-0 px-5 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeGroup?.id === g.id ? 'bg-slate-900 text-white shadow-sm' : 'bg-slate-50 text-slate-400 md:hover:bg-slate-100'}`}
                                    >
                                        {g.season}
                                        {g.window && g.window !== 'all' && (
                                            <span className="ml-1 opacity-60">
                                                ({g.window.toLowerCase().includes('summer') ? t(lang, 'summer') : t(lang, 'winter')})
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {filteredData.length > 0 ? (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-4">
                            {filteredData.map((item, idx) => (
                                <TransferCard 
                                    key={item.id} 
                                    transfer={item} 
                                    rank={idx} 
                                    onClick={() => onNavigateToPlayer(item.playerId || item.id)} 
                                    showDate={activeGroup?.id === 'latest'}
                                    hideRank={activeGroup?.id === 'latest'}
                                />
                            ))}
                        </div>

                        {activeGroup?.id === 'latest' && hasMore && (
                            <div className="pt-8 flex justify-center">
                                <button 
                                    onClick={loadMore}
                                    disabled={loading}
                                    className="px-8 py-3 bg-slate-900 text-white rounded-2xl text-xs font-black uppercase tracking-widest md:hover:bg-slate-800 transition-all disabled:opacity-50 flex items-center gap-2"
                                >
                                    {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                                    {t(lang, 'loadMoreNews')}
                                </button>
                            </div>
                        )}
                    </div>
                ) : !loading ? (
                    <div className="py-32 text-center flex flex-col items-center gap-4 opacity-30">
                        <Info size={48} />
                        <p className="text-sm font-black uppercase tracking-widest">No matching records found</p>
                    </div>
                ) : null}

                {loading && filteredData.length === 0 && (
                    <div className="py-20 flex flex-col items-center justify-center gap-4">
                        <Loader2 className="animate-spin text-rose-500" size={32} />
                        <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">{t(lang, 'synchronizingIntelligence')}</p>
                    </div>
                )}
                
                <div className="mt-8 flex justify-end">
                    <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">{t(lang, 'dataProvidedBy')}: transfermarkt</span>
                </div>
            </div>
        </div>
    );
};
